/**
 * Every number the app treats as clinically meaningful, in one file, each with the reason it is
 * that number. A threshold no one can justify in a comment does not belong here.
 *
 * These are not tuning constants. Changing one changes what the product tells a person with cancer
 * to do about a fever at midnight.
 */

import type { MetricKind } from './types.js';

/**
 * A single oral temperature of 38.0 °C or more, in a patient on treatment, is the febrile
 * neutropenia trigger every oncology service in the world works to. It is an emergency because the
 * infection can outrun the immune system within hours, and because the patient cannot tell from how
 * they feel whether their neutrophil count is on the floor. There is no "watch it for a bit".
 */
export const FEVER_C = 38.0;

/**
 * 37.5 °C sustained is the second trigger most protocols carry, and is worth saying something about
 * even though it is not the emergency. Below 38.0 the app raises attention, not an ambulance.
 */
export const FEVER_WATCH_C = 37.5;

/**
 * Sepsis does not always come with a fever: a temperature this low, in someone on treatment who
 * feels unwell, is the same phone call. Worth flagging rather than quietly recording as "normal".
 */
export const HYPOTHERMIA_C = 36.0;

/**
 * The nadir: the window after a cytotoxic dose when the neutrophil count is at its lowest and an
 * infection is most dangerous. Days 7 to 14 covers the common regimens. It changes nothing about
 * what a fever means — 38.0 is 38.0 on any day — it changes what the coach says to expect.
 */
export const NADIR_START_DAY = 7;
export const NADIR_END_DAY = 14;

/**
 * Grade 3 on the patient-reported scale is "severe": it stops them doing what they normally do.
 * That is the point at which a symptom stops being something to manage at home.
 */
export const SEVERE_GRADE = 3;

/**
 * Moderate, on the same scale. Two moderate symptoms that feed each other — vomiting and not
 * drinking — matter more than either alone, which is what the dehydration flag is about.
 */
export const MODERATE_GRADE = 2;

/** Below this in a day, with vomiting or diarrhoea, is how people end up on a drip. */
export const INTAKE_LOW_ML = 1000;

/**
 * Five per cent of body weight in a month is the definition of clinically significant unintentional
 * weight loss, and the threshold at which nutritional support is considered.
 */
export const WEIGHT_LOSS_PERCENT = 5;
export const WEIGHT_LOSS_WINDOW_DAYS = 30;

/** Two consecutive days of an oral anti-cancer therapy missed is a clinic conversation, not a nudge. */
export const MISSED_ORAL_DAYS = 2;

/** Nothing logged for a week: not a clinical finding, but the care team should know they went quiet. */
export const INACTIVE_DAYS = 7;

/**
 * How far back a flag looks. Long enough that a Friday symptom is still visible on Monday morning,
 * short enough that a dashboard shows this week rather than this month.
 */
export const FLAG_WINDOW_MS = 48 * 60 * 60 * 1000;

/** How much history the state carries. Everything older lives in the event log. */
export const METRIC_RETENTION_DAYS = 120;
export const SYMPTOM_RETENTION_DAYS = 120;
export const TEMP_RETENTION_DAYS = 120;

/**
 * What a human body can plausibly produce. These refuse a typo and a broken sensor; they are not
 * clinical ranges, and a value inside them can still be an emergency.
 */
export const TEMP_BOUNDS: readonly [number, number] = [30, 43];

export const METRIC_BOUNDS: Readonly<Record<MetricKind, readonly [number, number]>> = {
  'weight-lbs': [40, 800],
  'heart-rate': [25, 220],
  'sleep-hours': [0, 24],
  'exertion-minutes': [0, 1440],
  'intake-ml': [0, 10000],
  'bowel-movements': [0, 40],
};

export function withinBounds(kind: MetricKind, value: number): boolean {
  const [lo, hi] = METRIC_BOUNDS[kind];
  return Number.isFinite(value) && value >= lo && value <= hi;
}

export function temperatureWithinBounds(celsius: number): boolean {
  const [lo, hi] = TEMP_BOUNDS;
  return Number.isFinite(celsius) && celsius >= lo && celsius <= hi;
}
